/**
 * DashboardFooter — handoff §3.7.
 *
 * Compliance strip with the auto-generated QPP audit reference + 3 buttons:
 *
 *   - Export QPP packet      — disabled (PDF export deferred, V1 uses browser print)
 *   - Email summary          — disabled (deferred — out of V1 scope)
 *   - Schedule next review   — POST /v1/clinics/{cid}/meetings for the next quarter
 *
 * Per the user's confirmation on out-of-scope items: render the disabled
 * buttons rather than hide them so the UI surface matches the mockup and
 * the V2 work is visibly tracked.
 */
import { useCreateMeeting } from "@/hooks/useCreateMeeting";
import type { MeetingDetail } from "@/api/models";

export interface DashboardFooterProps {
  clinicId: number;
  year: number;
  meeting: MeetingDetail | null;
}

/**
 * Pick the next quarter to schedule. If the current meeting is Q2, the
 * footer button suggests Q3; if Q4, the next year's Q1. Defaults to the
 * current quarter if no meeting exists.
 */
function nextQuarter(currentQuarter: number | null): { year_delta: number; quarter: number } {
  const q = currentQuarter ?? Math.floor(new Date().getMonth() / 3) + 1;
  if (q === 4) return { year_delta: 1, quarter: 1 };
  return { year_delta: 0, quarter: q + 1 };
}

export function DashboardFooter({ clinicId, year, meeting }: DashboardFooterProps) {
  const create = useCreateMeeting(clinicId);

  const next = nextQuarter(meeting?.quarter ?? null);
  const nextYear = year + next.year_delta;
  const nextLabel = `Q${next.quarter} Review`;

  const onScheduleNext = () => {
    create.mutate({
      performance_year: nextYear,
      quarter: next.quarter,
      label: nextLabel,
      client_attendees: "",
    });
  };

  return (
    <div className="rounded-lg border border-border bg-white px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex-1 min-w-[300px]">
          <div className="text-xs text-gray-500">
            QPP Audit Reference{" "}
            {meeting?.qpp_audit_ref ? (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-700">
                {meeting.qpp_audit_ref}
              </span>
            ) : (
              <span className="text-gray-400">(generated on save)</span>
            )}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            Locks scoring snapshot, meeting metadata, committed goals.
            Preserved 6 years per QPP requirements.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled
            title="PDF export deferred to V2 — use browser print-to-PDF"
            className="cursor-not-allowed rounded-button border border-border bg-gray-50 px-3 py-1.5 text-sm font-medium text-gray-400"
          >
            Export QPP packet
          </button>
          <button
            type="button"
            disabled
            title="Email summary deferred to V2"
            className="cursor-not-allowed rounded-button border border-border bg-gray-50 px-3 py-1.5 text-sm font-medium text-gray-400"
          >
            Email summary
          </button>
          <button
            type="button"
            onClick={onScheduleNext}
            disabled={create.isPending}
            className="rounded-button bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-60"
          >
            {create.isPending ? "Scheduling…" : `Schedule ${nextLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}
